import React, { useState } from 'react';
import { Button, Typography, Image, Row, Col } from 'components';
import { CloseOutlined } from '@ant-design/icons';
import { useCaptureItem } from './useCaptureItem';
import { ProgressStepper } from '../components/ProgressStepper';
import { IdentifierImageBox, ItemStatusDisplay, AdditionalImages } from './components';
import styles from './styles.module.scss';

const { Text } = Typography;

export const CaptureItem: React.FC = () => {
  const {
    isLoading,
    isIdentifying,
    identifiedItem,
    uploadError,
    identificationImage,
    additionalImages,
    handleFileSelect,
    handleRetake,
    handleRemoveImage,
    handleNext,
    handleBack,
    handleRaiseClaim,
    handleWriteCode,
    completedCount,
    totalCount,
    fileInputRef,
  } = useCaptureItem();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const handleOpenCamera = () => {
    fileInputRef.current?.click();
  };

  const handlePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setPreviewOpen(true);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button type="text" icon={<CloseOutlined />} onClick={handleBack} className={styles.backButton} />
      </div>

      {/* Progress Stepper */}
      <ProgressStepper
        currentStep={identifiedItem && !isIdentifying ? 2 : 1}
        totalSteps={4}
        completedCount={completedCount}
        totalCount={totalCount}
        onBack={handleBack}
      />

      {/* Title */}
      <div className={styles.titleSection}>
        <Text className={styles.pageTitle}>Capture Item</Text>
      </div>

      <div className={styles.content}>
        {/* Main Section: Identifier Image + Status */}
        <Row gutter={[12, 12]}>
          <Col xs={10} sm={8} md={6}>
            <IdentifierImageBox
              isIdentifying={isIdentifying}
              identificationImage={identificationImage}
              onPreview={handlePreview}
              onOpenCamera={handleOpenCamera}
            />
          </Col>

          <Col xs={14} sm={16} md={18}>
            <ItemStatusDisplay
              isIdentifying={isIdentifying}
              identifiedItem={identifiedItem}
              uploadError={uploadError}
              onRetake={handleRetake}
              onRaiseClaim={handleRaiseClaim}
              onWriteCode={handleWriteCode}
            />
          </Col>
        </Row>

        {/* Additional Images */}
        <AdditionalImages images={additionalImages} onPreview={handlePreview} onDelete={handleRemoveImage} />
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />

      {/* Bottom Actions */}
      <div className={styles.bottomActions}>
        <Button color="primary" variant="outlined" size="large" onClick={handleOpenCamera} block>
          Take a Photo
        </Button>

        <Button
          type="primary"
          size="large"
          onClick={handleNext}
          disabled={!identifiedItem || isIdentifying || !!uploadError}
          block
        >
          Next
        </Button>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </div>
  );
};
